/**
 * Theme runtime — the single switch behind the CSS-variable token system in
 * ../theme.css. A theme is just a `data-theme` value on <html>; flipping it
 * re-points every `--slate-*`/`--sky-*`/`--accent` channel, so the whole UI
 * (and the board, which reads the same vars) re-themes without re-rendering.
 */

import { hostStorage } from "../host/hostStorage.js";

export type ThemeName = "islands-dark" | "classic-dark";

export const THEMES: ReadonlyArray<{ id: ThemeName; label: string }> = [
  { id: "islands-dark", label: "Islands Dark" },
  { id: "classic-dark", label: "Classic Dark" },
];

export const DEFAULT_THEME: ThemeName = "islands-dark";
const STORAGE_KEY = "archlang:theme";
const EVENT = "archlang:themechange";

function isTheme(v: unknown): v is ThemeName {
  return v === "islands-dark" || v === "classic-dark";
}

/** Current theme: the live <html data-theme>, falling back to the default. */
export function getTheme(): ThemeName {
  if (typeof document !== "undefined") {
    const attr = document.documentElement.getAttribute("data-theme");
    if (isTheme(attr)) return attr;
  }
  return DEFAULT_THEME;
}

/** Set the active theme: reflect onto <html>, persist, notify subscribers. */
export function applyTheme(name: ThemeName): void {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-theme", name);
  }
  hostStorage.setItem(STORAGE_KEY, name);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: name }));
  }
}

/**
 * Resolve and apply the startup theme — saved preference wins over the
 * no-FOUC default baked into index.html. Call once before first render.
 */
export function initTheme(): ThemeName {
  let name = DEFAULT_THEME;
  const saved = hostStorage.getItem(STORAGE_KEY);
  if (isTheme(saved)) name = saved;
  applyTheme(name);
  return name;
}

/** Subscribe to theme changes (used by the React hook). */
export function subscribeTheme(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(EVENT, cb);
  return () => window.removeEventListener(EVENT, cb);
}
